import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Funcție logout stabilă (nu se recreează la fiecare render)
  const logout = useCallback((showMessage = false, message = null) => {
    // Invalidăm sesiunea pe server (fire and forget)
    const currentToken = localStorage.getItem("token");
    if (currentToken) {
      axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` }
      }).catch(() => {}); // Ignorăm erorile
    }
    
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    
    if (showMessage && message) {
      toast.error(message);
    }
  }, []);

  // Configurăm interceptor pentru a detecta SESSION_CONFLICT
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const errorMessage = error.response?.data?.detail || "";
        
        // Verificăm dacă e eroare de sesiune pe alt dispozitiv
        if (error.response?.status === 401 && errorMessage.includes("SESSION_CONFLICT")) {
          logout(true, "Contul a fost autentificat pe alt dispozitiv. Te rugăm să te reconectezi.");
          
          // Redirecționăm la login
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      // Nu afișăm mesaj dacă e SESSION_CONFLICT (deja gestionat de interceptor)
      const errorMessage = error.response?.data?.detail || "";
      if (!errorMessage.includes("SESSION_CONFLICT")) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, phone) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, phone });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Wrapper pentru logout fără mesaj (pentru butoanele de logout)
  const logoutUser = () => logout(false);

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout: logoutUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
